import { useState } from 'react';
import { ErrorState, Loading } from '../components/DataState.tsx';
import { patchNote, useNotes } from '../api/notes.ts';
import { ApiError } from '../api/client.ts';
import type { NotePatch } from '../api/notes.ts';
import { useMe } from '../api/me.ts';
import { NoteList } from './book/NoteList.tsx';
import { NoteDetail } from './book/NoteDetail.tsx';
import { NoteSettings } from './book/NoteSettings.tsx';
import { NewNote } from './book/NewNote.tsx';

/**
 * C1 ~ C4-1 — Prayer Book.
 *
 * 화면 흐름 (설계 2026-08-27 기준)
 *
 *   C1 메인 목록  탭을 열면 여기. 전체 노트를 찾고 · 거르고 · 정렬합니다
 *     ＋ → C1-5 → C2 새 노트,  카드 → C3
 *   C3 기본 화면  제목 · 내용 · 진행 기록을 여기서 고칩니다
 *     ⋯ → C4 설정  영역 · 상태 · 중요도 · PLAN · 대표 · Private · 공유 · 삭제
 *
 * 영역 서랍(옛 C2~C6)이 통째로 사라져 화면 사이의 길이 한 줄로 곧아졌습니다.
 * 목록에서 나가는 문이 없으므로 «온 길로 되돌리기»를 위한 상태도 필요 없습니다 —
 * 노트에서 ‹ 를 누르면 언제나 목록입니다.
 */

type View =
  | { at: 'main' }
  | { at: 'note'; id: string }
  | { at: 'settings'; id: string }
  | { at: 'new' };

export function Book() {
  const [view, setView] = useState<View>({ at: 'main' });
  const [failure, setFailure] = useState<string | null>(null);

  const { notes, loading, error, reload } = useNotes();
  const { name } = useMe();

  const note = 'id' in view ? notes.find((n) => n.id === view.id) : undefined;
  const representativeCount = notes.filter((n) => n.representative).length;

  /** 서버가 거절하면 그 문장을 그대로 띄웁니다. 대표 상한이 대표적인 경우입니다 */
  const applyPatch = async (id: string, changes: NotePatch) => {
    setFailure(null);
    try {
      await patchNote(id, changes);
      reload();
      return true;
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '저장하지 못했습니다.');
      return false;
    }
  };

  /* ── C2 새 노트 ─────────────────────────────────────────────── */

  if (view.at === 'new') {
    return (
      <NewNote
        author={name}
        onCancel={() => setView({ at: 'main' })}
        onSaved={(saved) => {
          reload();
          setView({ at: 'note', id: saved.id });
        }}
      />
    );
  }

  /* ── C4 설정 ────────────────────────────────────────────────── */

  if (view.at === 'settings' && note) {
    return (
      <NoteSettings
        note={note}
        representativeCount={representativeCount}
        author={name}
        onBack={() => setView({ at: 'note', id: note.id })}
        onPatch={(changes) => applyPatch(note.id, changes)}
        onDeleted={() => {
          reload();
          setView({ at: 'main' });
        }}
      />
    );
  }

  /* ── C3 기본 화면 ───────────────────────────────────────────── */

  if (view.at === 'note' && note) {
    return (
      <>
        <NoteDetail
          note={note}
          onBack={() => setView({ at: 'main' })}
          onSettings={() => setView({ at: 'settings', id: note.id })}
          onPatch={(changes) => applyPatch(note.id, changes)}
        />
        {failure && (
          <div className="screen__flash">
            <ErrorState message={failure} onRetry={() => setFailure(null)} />
          </div>
        )}
      </>
    );
  }

  /* ── C1 메인 목록 ───────────────────────────────────────────── */

  return (
    <>
      <NoteList
        notes={notes}
        onOpen={(id) => setView({ at: 'note', id })}
        onNew={() => setView({ at: 'new' })}
      />

      <div className="screen__flash">
        {loading && <Loading label="기도 노트를 불러오는 중" />}
        {error && <ErrorState message={error} onRetry={reload} />}
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}
      </div>
    </>
  );
}
